import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { 
  Check, 
  Crown, 
  Sparkles, 
  ArrowRight, 
  CreditCard, 
  Calendar,
  Star,
  Zap,
  Building2,
  Loader2,
  ChevronDown,
  ChevronUp
} from "lucide-react";

import { API_URL } from "@/utils/api";
const CALENDAR_LINK = "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ07qBLS9ridhleyYbHgoh8tK2GytA06zF8p0i5zF3IfiDRKXQGW17EZmfPsPcjIyZXiw-BjNNVw";

// All available plans
const PLANS = [
  { 
    id: "starter", 
    name: "Free / Starter", 
    monthly: 0, 
    annual: 0, 
    description: "For creators just getting started",
    icon: Star,
    color: "gray",
    features: [
      "Up to $25K annual transactions",
      "Basic bookkeeping",
      "Invoice generator",
      "Expense tracking",
      "Document uploads"
    ],
    highlight: null,
    cta: "Current Plan"
  },
  { 
    id: "growth", 
    name: "Growth", 
    monthly: 149, 
    annual: 1490, 
    description: "For growing businesses",
    icon: Zap,
    color: "emerald",
    features: [
      "Up to $100K annual transactions",
      "Full bookkeeping + reconciliation",
      "KPI dashboards",
      "Dedicated bookkeeper",
      "Bank connections",
      "Team management (up to 3)",
      "Contracts & Tax Hub"
    ],
    highlight: "Most Popular",
    cta: "Upgrade"
  },
  { 
    id: "scale", 
    name: "Scale", 
    monthly: 349, 
    annual: 3490, 
    description: "For established businesses",
    icon: Crown,
    color: "purple",
    features: [
      "Unlimited transactions",
      "Advanced reporting",
      "Tax strategy planning",
      "Priority support",
      "S-Corp Election assistance",
      "Team management (up to 10)",
      "All Growth features"
    ],
    highlight: null,
    cta: "Upgrade"
  },
  { 
    id: "enterprise", 
    name: "Enterprise", 
    monthly: 999, 
    annual: 9990, 
    description: "For complex operations",
    icon: Building2,
    color: "amber",
    features: [
      "Everything in Scale",
      "Dedicated account manager",
      "Custom integrations",
      "CFO advisory services",
      "Unlimited team members",
      "24/7 priority support",
      "Custom reporting"
    ],
    highlight: "Custom Pricing",
    cta: "Contact Sales"
  }
];

const PLAN_ORDER = ["free", "starter", "growth", "scale", "enterprise"];

export default function PlansPage({ user }) {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "upgrade" or "downgrade"

  const currentPlan = user?.plan || "free";
  const currentPlanIndex = PLAN_ORDER.indexOf(currentPlan === "free" ? "starter" : currentPlan);

  const getPlanIndex = (planId) => {
    return PLAN_ORDER.indexOf(planId === "free" ? "starter" : planId);
  };

  const getActionType = (planId) => {
    const targetIndex = getPlanIndex(planId);
    if (targetIndex > currentPlanIndex) return "upgrade";
    if (targetIndex < currentPlanIndex) return "downgrade";
    return "current";
  };

  const handlePlanAction = (plan) => {
    const action = getActionType(plan.id);
    
    if (action === "current") {
      toast.info("You're already on this plan");
      return;
    }
    
    if (plan.id === "enterprise") {
      window.open(CALENDAR_LINK, "_blank");
      return;
    }

    setSelectedPlan(plan);
    setConfirmAction(action);
    setShowConfirmModal(true);
  };

  const handleCheckout = async () => {
    if (!selectedPlan) return;
    
    // For downgrade to free, just update the plan directly
    if (selectedPlan.id === "starter" || selectedPlan.monthly === 0) {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      try {
        // For now, downgrades would happen at end of billing cycle
        // This is a simplified implementation
        toast.info("Your plan will be downgraded at the end of your billing cycle");
        setShowConfirmModal(false);
        setLoading(false);
        return;
      } catch (err) {
        toast.error("Failed to process request");
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    const token = localStorage.getItem("auth_token");
    
    try {
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          ...(token ? { Authorization: `Bearer ${token}` } : {}), 
          "Origin": window.location.origin 
        },
        credentials: "include",
        body: JSON.stringify({ 
          plan_id: selectedPlan.id, 
          billing_cycle: billingCycle 
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create checkout");
      }
      
      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.message);
      setLoading(false);
    }
  };

  const getButtonText = (plan) => {
    const action = getActionType(plan.id);
    if (action === "current") return "Current Plan";
    if (plan.id === "enterprise") return "Contact Sales";
    if (action === "upgrade") return `Upgrade to ${plan.name}`;
    return `Switch to ${plan.name}`;
  };

  const getButtonStyle = (plan) => {
    const action = getActionType(plan.id);
    if (action === "current") {
      return "bg-[#121212] text-white cursor-default";
    }
    if (plan.highlight === "Most Popular") {
      return "bg-[#BFFF00] hover:bg-[#a8e600] text-[#121212]";
    }
    return "bg-[#121212] hover:bg-[#2a2a2a] text-white";
  };

  const formatPrice = (plan) => {
    if (plan.monthly === 0) return "Free";
    if (plan.id === "enterprise") return "Custom";
    return billingCycle === "annual" 
      ? `$${(plan.annual / 12).toFixed(0)}/mo` 
      : `$${plan.monthly}/mo`;
  };

  const getAnnualSavings = (plan) => {
    if (plan.monthly === 0 || plan.id === "enterprise") return null;
    const monthlyCost = plan.monthly * 12;
    const savings = monthlyCost - plan.annual;
    return savings > 0 ? savings : null;
  };

  return (
    <DashboardLayout user={user}>
      <div className="max-w-6xl mx-auto space-y-8" data-testid="plans-page">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#121212] font-['Manrope']">
            Choose Your Plan
          </h1>
          <p className="text-[#121212]/60 mt-2">
            Currently on <span className="font-semibold text-[#121212] capitalize">{currentPlan === "free" ? "Starter" : currentPlan}</span> plan
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center">
          <div className="bg-[#F7FFE5] border border-[#BFFF00]/30 p-1.5 rounded-full flex items-center gap-1">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                billingCycle === "monthly" 
                  ? "bg-[#121212] text-white" 
                  : "text-[#121212]/70 hover:text-[#121212]"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                billingCycle === "annual" 
                  ? "bg-[#121212] text-white" 
                  : "text-[#121212]/70 hover:text-[#121212]"
              }`}
            >
              Annual
              <Badge className="bg-[#BFFF00] text-[#121212] text-xs rounded-full">Save 17%</Badge>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLANS.map((plan) => {
            const action = getActionType(plan.id);
            const isCurrent = action === "current";
            const Icon = plan.icon;
            const savings = getAnnualSavings(plan);
            
            return (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden rounded-2xl transition-all duration-300 ${
                  isCurrent 
                    ? "border-2 border-[#BFFF00] shadow-lg shadow-[#BFFF00]/20" 
                    : plan.highlight === "Most Popular"
                    ? "border-2 border-[#BFFF00]/50 hover:border-[#BFFF00] hover:shadow-lg"
                    : "border-2 border-dashed border-[#121212]/10 hover:border-[#BFFF00]/50"
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {/* Highlight Badge */}
                {plan.highlight && (
                  <div className="absolute top-0 right-0">
                    <Badge className={`rounded-none rounded-bl-xl px-3 py-1 text-xs font-semibold ${
                      plan.highlight === "Most Popular" 
                        ? "bg-[#BFFF00] text-[#121212]" 
                        : "bg-[#121212] text-white"
                    }`}>
                      {plan.highlight}
                    </Badge>
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrent && (
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-[#121212] text-white rounded-full text-xs">
                      Your Plan
                    </Badge>
                  </div>
                )}

                <CardContent className="p-6 pt-10">
                  {/* Plan Icon & Name */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      plan.color === "gray" ? "bg-gray-100" :
                      plan.color === "emerald" ? "bg-emerald-100" :
                      plan.color === "purple" ? "bg-purple-100" :
                      "bg-amber-100"
                    }`}>
                      <Icon className={`w-6 h-6 ${
                        plan.color === "gray" ? "text-gray-600" :
                        plan.color === "emerald" ? "text-emerald-600" :
                        plan.color === "purple" ? "text-purple-600" :
                        "text-amber-600"
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#121212] font-['Manrope']">{plan.name}</h3>
                      <p className="text-xs text-[#121212]/60">{plan.description}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-[#121212]">
                        {formatPrice(plan)}
                      </span>
                      {plan.monthly > 0 && plan.id !== "enterprise" && billingCycle === "annual" && (
                        <span className="text-sm text-[#121212]/50 line-through">
                          ${plan.monthly}/mo
                        </span>
                      )}
                    </div>
                    {billingCycle === "annual" && savings && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Save ${savings}/year
                      </p>
                    )}
                    {billingCycle === "annual" && plan.monthly > 0 && plan.id !== "enterprise" && (
                      <p className="text-xs text-[#121212]/50 mt-1">
                        Billed ${plan.annual.toLocaleString()}/year
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-2.5 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-[#121212]/80">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button */}
                  <Button
                    onClick={() => handlePlanAction(plan)}
                    disabled={isCurrent}
                    className={`w-full rounded-full font-semibold ${getButtonStyle(plan)}`}
                    data-testid={`plan-action-${plan.id}`}
                  >
                    {getButtonText(plan)}
                    {!isCurrent && plan.id !== "enterprise" && (
                      <ArrowRight className="w-4 h-4 ml-2" />
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ / Info Section */}
        <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#F7FFE5] rounded-xl flex items-center justify-center mx-auto mb-3">
                  <CreditCard className="w-6 h-6 text-[#121212]" />
                </div>
                <h4 className="font-bold text-[#121212] mb-1">Secure Payments</h4>
                <p className="text-sm text-[#121212]/60">Powered by Stripe with bank-level security</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#F7FFE5] rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Calendar className="w-6 h-6 text-[#121212]" />
                </div>
                <h4 className="font-bold text-[#121212] mb-1">Flexible Billing</h4>
                <p className="text-sm text-[#121212]/60">Switch plans anytime, changes apply next cycle</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#F7FFE5] rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-[#121212]" />
                </div>
                <h4 className="font-bold text-[#121212] mb-1">No Hidden Fees</h4>
                <p className="text-sm text-[#121212]/60">What you see is what you pay, always</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact CTA */}
        <div className="text-center py-8">
          <p className="text-[#121212]/60 mb-4">
            Need help choosing the right plan? Our team is here to help.
          </p>
          <Button
            variant="outline"
            onClick={() => window.open(CALENDAR_LINK, "_blank")}
            className="rounded-full border-[#121212] hover:bg-[#121212] hover:text-white"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Schedule a Call
          </Button>
        </div>
      </div>

      {/* Confirm Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="rounded-2xl border-2 border-[#BFFF00]/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope'] text-xl">
              {confirmAction === "upgrade" ? "Confirm Upgrade" : "Confirm Plan Change"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPlan && (
            <div className="py-4 space-y-4">
              <div className="bg-[#F7FFE5] rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#121212]/60">New Plan</span>
                  <span className="font-bold text-[#121212]">{selectedPlan.name}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#121212]/60">Billing</span>
                  <span className="font-medium text-[#121212] capitalize">{billingCycle}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[#BFFF00]/30">
                  <span className="text-[#121212]/60">Price</span>
                  <span className="font-bold text-[#121212] text-lg">
                    {billingCycle === "annual" 
                      ? `$${selectedPlan.annual?.toLocaleString()}/year`
                      : `$${selectedPlan.monthly}/month`
                    }
                  </span>
                </div>
              </div>

              {confirmAction === "upgrade" && (
                <p className="text-sm text-[#121212]/60">
                  You'll be redirected to Stripe to complete your payment securely. 
                  Your new plan features will be available immediately after payment.
                </p>
              )}

              {confirmAction === "downgrade" && (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-xl">
                  Your plan will change at the end of your current billing cycle. 
                  You'll continue to have access to your current features until then.
                </p>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCheckout}
              disabled={loading}
              className="flex-1 rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {confirmAction === "upgrade" ? "Proceed to Payment" : "Confirm Change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
